import { Stack } from "@mui/material";
import { useMemo } from "react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import {
  buildMedicationInsightCharts,
  buildMedicationPatientRows,
  MEDICATION_STATUS_TRANSLATION,
} from "src-next/modules/clinical-insights/transformers/mapMedicationInsights";
import { matchesPractitionerFilter } from "src-next/modules/clinical-insights/helpers/filterResourcesByPractitioner";
import { fetchInsightMedicationRequests } from "src-next/modules/clinical-insights/services/fetchInsightOrganizationBundle";
import { useFhirInsightView } from "src-next/modules/clinical-insights/hooks/useFhirInsightView";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";

const texts = getInsightAnalysisTexts("tratamentos");

function medicationPatientIds(requests: fhir5.MedicationRequest[]): string[] {
  return requests.map((request) => request.subject?.reference || "");
}

export function PrescriptionAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
  fetchWarm,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const practitionerFilter = practitionerId || "all";
  const warm = fetchWarm ?? isActive;

  const view = useFhirInsightView({
    analysisId: "tratamentos",
    organizationId: organizationId || "",
    dateRange,
    enabled: warm,
    isActive,
    onLoadPhaseChange,
    selectPatientIds: medicationPatientIds,
    fetcher: () =>
      fetchInsightMedicationRequests({
        organizationId: organizationId || "",
        startDate: dateRange?.startDate,
        endDate: dateRange?.endDate,
      }),
  });

  const medications = useMemo(() => {
    if (practitionerFilter === "all") return view.resources;
    return view.resources.filter((med) =>
      matchesPractitionerFilter(practitionerFilter, med.requester?.reference)
    );
  }, [view.resources, practitionerFilter]);

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const { statusItems, rankingItems, total } = useMemo(
    () => buildMedicationInsightCharts(medications),
    [medications]
  );

  const patientRows = useMemo(
    () => buildMedicationPatientRows(medications, view.patients),
    [medications, view.patients]
  );

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% das ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, total]);

  const busy = view.isLoading || view.patientsLoading;

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#4caf50"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={view.loadPhase}
        patientsProgress={view.patientsProgress}
        hasData={view.hasPayload || medications.length > 0}
        isEmpty={total === 0 && !busy}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={() => {
          void view.refresh();
        }}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={MEDICATION_STATUS_TRANSLATION}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
